import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../index.js';
import apiClient from '../../shared/services/apiClient.js';

// Types for ML Model Registry
export interface ModelRegistry {
  id: string;
  name: string;
  version: string;
  display_name?: string;
  description?: string;
  model_type: 'classification' | 'regression' | 'clustering' | 'anomaly_detection' | 'nlp' | 'computer_vision' | 'time_series' | 'ensemble';
  status: 'training' | 'trained' | 'validating' | 'validated' | 'deploying' | 'deployed' | 'active' | 'deprecated' | 'failed' | 'archived';
  framework: string;
  algorithm: string;
  hyperparameters: Record<string, unknown>;
  training_config: Record<string, unknown>;
  performance_metrics: Record<string, unknown>;
  validation_metrics: Record<string, unknown>;
  feature_importance: Record<string, unknown>;
  deployment_config: Record<string, unknown>;
  deployment_status: 'pending' | 'deploying' | 'deployed' | 'failed' | 'stopped';
  endpoint_url?: string;
  is_active: boolean;
  auto_scale_config: Record<string, unknown>;
  model_path?: string;
  model_size_bytes?: number;
  model_checksum?: string;
  total_predictions: number;
  avg_response_time_ms?: number;
  error_rate?: number;
  training_started_at?: string;
  training_completed_at?: string;
  validation_completed_at?: string;
  deployed_at?: string;
  last_inference_at?: string;
  retired_at?: string;
  created_at: string;
  updated_at: string;
}

export interface ModelVersion {
  id: string;
  version: string;
  parent_version?: string;
  is_current: boolean;
  changes_description?: string;
  performance_diff: Record<string, unknown>;
  created_at: string;
}

export interface ModelMetrics {
  model_id: string;
  model_name: string;
  version: string;
  time_range: string;
  metrics: {
    total_inferences: number;
    successful_inferences: number;
    failed_inferences: number;
    avg_response_time_ms: number;
    success_rate: number;
    error_rate: number;
    total_predictions_lifetime: number;
  };
  performance_metrics: Record<string, unknown>;
  validation_metrics: Record<string, unknown>;
}

export interface InferenceHealth {
  status: 'healthy' | 'unhealthy';
  timestamp: string;
  tenant_id: string;
  active_models: number;
  engine_status: Record<string, unknown>;
  cache_size: number;
  error?: string;
}

export interface DeploymentStatus {
  deployment_id: string;
  model_id: string;
  deployment_name: string;
  status: 'pending' | 'deploying' | 'deployed' | 'failed' | 'stopped';
  message: string;
}

// State interface
export interface MLState {
  // Model Registry
  models: ModelRegistry[];
  selectedModel: ModelRegistry | null;
  modelsLoading: boolean;
  modelsError: string | null;
  modelsPagination: {
    total: number;
    skip: number;
    limit: number;
    pages: number;
  };
  
  // Model Versions
  modelVersions: Record<string, ModelVersion[]>;
  versionsLoading: boolean;
  versionsError: string | null;
  
  // Model Metrics
  modelMetrics: Record<string, ModelMetrics>;
  metricsLoading: boolean;
  metricsError: string | null;
  
  // Deployment Management
  deploymentStatus: Record<string, DeploymentStatus>;
  deploymentLoading: boolean;
  deploymentError: string | null;
  
  // Inference Health
  inferenceHealth: InferenceHealth | null;
  healthLoading: boolean;
  healthError: string | null;
  
  // UI State
  filters: {
    model_type?: string;
    status?: string;
    is_active?: boolean;
    search?: string;
  };
  sortBy: string;
  sortOrder: 'asc' | 'desc';
}

const initialState: MLState = {
  models: [],
  selectedModel: null,
  modelsLoading: false,
  modelsError: null,
  modelsPagination: {
    total: 0,
    skip: 0,
    limit: 100,
    pages: 0
  },
  modelVersions: {},
  versionsLoading: false,
  versionsError: null,
  modelMetrics: {},
  metricsLoading: false,
  metricsError: null,
  deploymentStatus: {},
  deploymentLoading: false,
  deploymentError: null,
  inferenceHealth: null,
  healthLoading: false,
  healthError: null,
  filters: {},
  sortBy: 'created_at',
  sortOrder: 'desc'
};

// Async Thunks
export const fetchModels = createAsyncThunk(
  'ml/fetchModels',
  async (params: {
    skip?: number;
    limit?: number;
    model_type?: string;
    status?: string;
    is_active?: boolean;
    search?: string;
    sort_by?: string;
    sort_order?: string;
  } = {}) => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        queryParams.append(key, value.toString());
      }
    });
    
    const response = await apiClient.get(`/api/ml/registry/models?${queryParams}`);
    return response.data;
  }
);

export const fetchModelById = createAsyncThunk(
  'ml/fetchModelById',
  async (params: { modelId: string; includeVersions?: boolean; includeMetrics?: boolean }) => {
    const queryParams = new URLSearchParams();
    if (params.includeVersions) queryParams.append('include_versions', 'true');
    if (params.includeMetrics) queryParams.append('include_metrics', 'true');
    
    const response = await apiClient.get(`/api/ml/registry/models/${params.modelId}?${queryParams}`);
    return response.data;
  }
);

export const createModel = createAsyncThunk(
  'ml/createModel',
  async (modelData: Partial<ModelRegistry>) => {
    const response = await apiClient.post('/api/ml/registry/models', modelData);
    return response.data;
  }
);

export const updateModel = createAsyncThunk(
  'ml/updateModel',
  async (params: { modelId: string; modelData: Partial<ModelRegistry> }) => {
    const response = await apiClient.put(`/api/ml/registry/models/${params.modelId}`, params.modelData);
    return response.data;
  }
);

export const archiveModel = createAsyncThunk(
  'ml/archiveModel',
  async (modelId: string) => {
    const response = await apiClient.delete(`/api/ml/registry/models/${modelId}`);
    return { modelId, ...response.data };
  }
);

export const deployModel = createAsyncThunk(
  'ml/deployModel',
  async (params: { modelId: string; deploymentData: Record<string, unknown> }) => {
    const response = await apiClient.post(`/api/ml/registry/models/${params.modelId}/deploy`, params.deploymentData);
    return response.data;
  }
);

export const fetchModelMetrics = createAsyncThunk(
  'ml/fetchModelMetrics',
  async (params: { modelId: string; timeRange?: string }) => {
    const queryParams = new URLSearchParams();
    if (params.timeRange) queryParams.append('time_range', params.timeRange);
    
    const response = await apiClient.get(`/api/ml/registry/models/${params.modelId}/metrics?${queryParams}`);
    return response.data;
  }
);

export const fetchInferenceHealth = createAsyncThunk(
  'ml/fetchInferenceHealth',
  async () => {
    const response = await apiClient.get('/inference/inference/health');
    return response.data;
  }
);

export const performInference = createAsyncThunk(
  'ml/performInference',
  async (params: { modelId: string; inputData: Record<string, unknown> }) => {
    const response = await apiClient.post(`/inference/models/${params.modelId}/predict`, params.inputData);
    return response.data;
  }
);

export const performBatchInference = createAsyncThunk(
  'ml/performBatchInference',
  async (params: { modelId: string; inputs: Record<string, unknown>[] }) => {
    const response = await apiClient.post('/inference/batch', {
      model_id: params.modelId,
      inputs: params.inputs
    });
    return response.data;
  }
);

// Slice
const mlSlice = createSlice({
  name: 'ml',
  initialState,
  reducers: {
    setSelectedModel: (state, action: PayloadAction<ModelRegistry | null>) => {
      state.selectedModel = action.payload;
    },
    setFilters: (state, action: PayloadAction<Partial<MLState['filters']>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    setSorting: (state, action: PayloadAction<{ sortBy: string; sortOrder: 'asc' | 'desc' }>) => {
      state.sortBy = action.payload.sortBy;
      state.sortOrder = action.payload.sortOrder;
    },
    clearModelsError: (state) => {
      state.modelsError = null;
    },
    clearMetricsError: (state) => {
      state.metricsError = null;
    },
    clearDeploymentError: (state) => {
      state.deploymentError = null;
    },
    clearHealthError: (state) => {
      state.healthError = null;
    }
  },
  extraReducers: (builder) => {
    // Fetch Models
    builder
      .addCase(fetchModels.pending, (state) => {
        state.modelsLoading = true;
        state.modelsError = null;
      })
      .addCase(fetchModels.fulfilled, (state, action) => {
        state.modelsLoading = false;
        state.models = action.payload.models;
        state.modelsPagination = action.payload.pagination;
      })
      .addCase(fetchModels.rejected, (state, action) => {
        state.modelsLoading = false;
        state.modelsError = action.error.message || 'Failed to fetch models';
      });

    // Fetch Model by ID
    builder
      .addCase(fetchModelById.pending, (state) => {
        state.modelsLoading = true;
        state.modelsError = null;
      })
      .addCase(fetchModelById.fulfilled, (state, action) => {
        state.modelsLoading = false;
        state.selectedModel = action.payload;
        if (action.payload.versions) {
          state.modelVersions[action.payload.id] = action.payload.versions;
        }
        if (action.payload.recent_metrics) {
          state.modelMetrics[action.payload.id] = {
            model_id: action.payload.id,
            model_name: action.payload.name,
            version: action.payload.version,
            time_range: '24h',
            metrics: action.payload.recent_metrics,
            performance_metrics: action.payload.performance_metrics,
            validation_metrics: action.payload.validation_metrics
          };
        }
      })
      .addCase(fetchModelById.rejected, (state, action) => {
        state.modelsLoading = false;
        state.modelsError = action.error.message || 'Failed to fetch model';
      });

    // Create Model
    builder
      .addCase(createModel.pending, (state) => {
        state.modelsLoading = true;
        state.modelsError = null;
      })
      .addCase(createModel.fulfilled, (state, action) => {
        state.modelsLoading = false;
        // Refresh models list after creation
      })
      .addCase(createModel.rejected, (state, action) => {
        state.modelsLoading = false;
        state.modelsError = action.error.message || 'Failed to create model';
      });

    // Update Model
    builder
      .addCase(updateModel.pending, (state) => {
        state.modelsLoading = true;
        state.modelsError = null;
      })
      .addCase(updateModel.fulfilled, (state, action) => {
        state.modelsLoading = false;
        // Update model in the list
        const index = state.models.findIndex(model => model.id === action.payload.id);
        if (index !== -1) {
          state.models[index] = { ...state.models[index], ...action.payload };
        }
        if (state.selectedModel?.id === action.payload.id) {
          state.selectedModel = { ...state.selectedModel, ...action.payload };
        }
      })
      .addCase(updateModel.rejected, (state, action) => {
        state.modelsLoading = false;
        state.modelsError = action.error.message || 'Failed to update model';
      });

    // Archive Model
    builder
      .addCase(archiveModel.pending, (state) => {
        state.modelsLoading = true;
        state.modelsError = null;
      })
      .addCase(archiveModel.fulfilled, (state, action) => {
        state.modelsLoading = false;
        // Update model status to archived
        const index = state.models.findIndex(model => model.id === action.payload.modelId);
        if (index !== -1) {
          state.models[index].status = 'archived';
          state.models[index].is_active = false;
        }
      })
      .addCase(archiveModel.rejected, (state, action) => {
        state.modelsLoading = false;
        state.modelsError = action.error.message || 'Failed to archive model';
      });

    // Deploy Model
    builder
      .addCase(deployModel.pending, (state) => {
        state.deploymentLoading = true;
        state.deploymentError = null;
      })
      .addCase(deployModel.fulfilled, (state, action) => {
        state.deploymentLoading = false;
        state.deploymentStatus[action.payload.model_id] = action.payload;
      })
      .addCase(deployModel.rejected, (state, action) => {
        state.deploymentLoading = false;
        state.deploymentError = action.error.message || 'Failed to deploy model';
      });

    // Fetch Model Metrics
    builder
      .addCase(fetchModelMetrics.pending, (state) => {
        state.metricsLoading = true;
        state.metricsError = null;
      })
      .addCase(fetchModelMetrics.fulfilled, (state, action) => {
        state.metricsLoading = false;
        state.modelMetrics[action.payload.model_id] = action.payload;
      })
      .addCase(fetchModelMetrics.rejected, (state, action) => {
        state.metricsLoading = false;
        state.metricsError = action.error.message || 'Failed to fetch metrics';
      });

    // Fetch Inference Health
    builder
      .addCase(fetchInferenceHealth.pending, (state) => {
        state.healthLoading = true;
        state.healthError = null;
      })
      .addCase(fetchInferenceHealth.fulfilled, (state, action) => {
        state.healthLoading = false;
        state.inferenceHealth = action.payload;
      })
      .addCase(fetchInferenceHealth.rejected, (state, action) => {
        state.healthLoading = false;
        state.healthError = action.error.message || 'Failed to fetch health status';
      });
  }
});

// Actions
export const {
  setSelectedModel,
  setFilters,
  setSorting,
  clearModelsError,
  clearMetricsError,
  clearDeploymentError,
  clearHealthError
} = mlSlice.actions;

// Selectors
export const selectModels = (state: RootState) => state.ml.models;
export const selectSelectedModel = (state: RootState) => state.ml.selectedModel;
export const selectModelsLoading = (state: RootState) => state.ml.modelsLoading;
export const selectModelsError = (state: RootState) => state.ml.modelsError;
export const selectModelsPagination = (state: RootState) => state.ml.modelsPagination;

export const selectModelVersions = (modelId: string) => (state: RootState) =>
  state.ml.modelVersions[modelId] || [];

export const selectModelMetrics = (modelId: string) => (state: RootState) =>
  state.ml.modelMetrics[modelId];

export const selectDeploymentStatus = (modelId: string) => (state: RootState) =>
  state.ml.deploymentStatus[modelId];

export const selectInferenceHealth = (state: RootState) => state.ml.inferenceHealth;
export const selectHealthLoading = (state: RootState) => state.ml.healthLoading;
export const selectHealthError = (state: RootState) => state.ml.healthError;

export const selectFilters = (state: RootState) => state.ml.filters;
export const selectSorting = (state: RootState) => ({
  sortBy: state.ml.sortBy,
  sortOrder: state.ml.sortOrder
});

export default mlSlice.reducer;
